import SysColumn from "app/class/sys-column";
import { FieldRuntimeProps } from "../types/record-producer-types";

export type FormSection = { id: string; label: string; order: number; active: boolean; layout_columns?: string | number; render_mode?: string; label_key?: string; show_label?: boolean | null; sys_form_view_id?: string | null };
export type FormView = { id: string; label: string; order: number; active: boolean; number_ref?: string; is_default?: boolean; inherited_from_table?: string; inherited_from_table_label?: string };
export function resolveDefaultFormViewId(views: FormView[]): string | null {
    return views.filter(view => view.is_default === true)
        .sort((a, b) => Number(a.order) - Number(b.order) || a.id.localeCompare(b.id))[0]?.id ?? null;
}
export function resolveFormViewId(views: FormView[], ...candidates: Array<string | null | undefined>): string | null {
    const ids = new Set(views.map(view => view.id));
    return candidates.find(candidate => typeof candidate === "string" && candidate !== "" && ids.has(candidate)) ?? null;
}
export type FormElement = { id?: string; sys_form_section_id: string; sys_column_id: string; column_index?: string | number | null; order: number };
export type FormLayoutData = { sections: FormSection[]; elements: FormElement[] };
export type InheritedFormFields = { childColumnIds: string[]; childLabel: string };
export type BuiltFormSection = { id: string; label: string; showLabel?: boolean; kind: "configured" | "other"; columns: SysColumn[]; twoColumns?: [SysColumn[], SysColumn[]]; tabGroup?: string };
export type FormLayoutBlock =
    | { type: "section"; section: BuiltFormSection }
    | { type: "tabs"; id: string; sections: BuiltFormSection[] };

export function groupFormSections(sections: BuiltFormSection[]): FormLayoutBlock[] {
    const blocks: FormLayoutBlock[] = [];
    let grouped: BuiltFormSection[] = [];
    const flush = () => {
        if (grouped.length > 1) blocks.push({ type: "tabs", id: grouped[0].tabGroup!, sections: grouped });
        else if (grouped.length === 1) blocks.push({ type: "section", section: grouped[0] });
        grouped = [];
    };
    sections.forEach(section => {
        if (!section.tabGroup || section.kind === "other") {
            flush();
            blocks.push({ type: "section", section });
        } else {
            if (grouped.length && grouped[0].tabGroup !== section.tabGroup) flush();
            grouped.push(section);
        }
    });
    flush();
    return blocks;
}

export function buildFormLayout(
    columns: SysColumn[],
    layoutData: FormLayoutData | null,
    runtimeStates: Record<string, FieldRuntimeProps>,
    viewId: string | null = null,
    defaultViewId: string | null = null,
    inheritedFields?: InheritedFormFields
): BuiltFormSection[] | null {
    const sections = layoutData?.sections.filter(section => section.active && (section.sys_form_view_id ?? defaultViewId ?? null) === viewId);
    if (!sections?.length) return null;

    const byId = new Map(columns.map(column => [column.id, column]));
    const used = new Set<string>();
    const result: BuiltFormSection[] = [];
    const visible = (column: SysColumn) => !(runtimeStates[column.name]?.hidden ?? column.is_hidden);

    const orderedSections = [...sections].sort((a, b) => Number(a.order) - Number(b.order));
    const tabGroups = new Map<string, string>();
    for (let index = 0; index < orderedSections.length;) {
        if (orderedSections[index].render_mode !== "tab") { index += 1; continue; }
        const start = index;
        while (index < orderedSections.length && orderedSections[index].render_mode === "tab") index += 1;
        if (index - start >= 2) {
            for (let member = start; member < index; member += 1) {
                tabGroups.set(orderedSections[member].id, orderedSections[start].id);
            }
        }
    }
    orderedSections.forEach(section => {
        const sectionColumns: SysColumn[] = [];
        const first: SysColumn[] = [];
        const second: SysColumn[] = [];
        layoutData!.elements
            .filter(element => element.sys_form_section_id === section.id)
            .sort((a, b) => Number(a.column_index) - Number(b.column_index) || Number(a.order) - Number(b.order))
            .forEach(element => {
                const column = byId.get(element.sys_column_id);
                if (!column || used.has(column.id)) return;
                used.add(column.id);
                sectionColumns.push(column);
                (Number(element.column_index) === 2 ? second : first).push(column);
            });
        if (sectionColumns.some(visible)) result.push({
            id: section.id, label: section.label, kind: "configured", columns: sectionColumns,
            ...(section.show_label === false ? { showLabel: false } : {}),
            ...(String(section.layout_columns) === "2" ? { twoColumns: [first, second] as [SysColumn[], SysColumn[]] } : {}),
            ...(tabGroups.has(section.id) ? { tabGroup: tabGroups.get(section.id) } : {}),
        });
    });

    if (inheritedFields) {
        const childIds = new Set(inheritedFields.childColumnIds);
        const childColumns = columns.filter(column => childIds.has(column.id) && !used.has(column.id) && visible(column))
            .sort((a, b) => a.order - b.order);
        if (childColumns.length) {
            const twoColumn = orderedSections.some(section => String(section.layout_columns) === "2");
            result.push({
                id: "inherited-child-fields", label: inheritedFields.childLabel, kind: "configured", columns: childColumns,
                ...(twoColumn ? { twoColumns: [childColumns.filter((_, index) => index % 2 === 0), childColumns.filter((_, index) => index % 2 === 1)] as [SysColumn[], SysColumn[]] } : {}),
            });
            childColumns.forEach(column => used.add(column.id));
        }
    }

    if (!result.length) return null;
    const other = [...columns].sort((a, b) => a.order - b.order).filter(column =>
        !used.has(column.id) && column.name !== "sys_class_name" && visible(column) && (runtimeStates[column.name]?.required ?? column.is_mandatory)
    );
    if (other.length) result.push({ id: "other", label: "", kind: "other", columns: other });
    return result.length ? result : null;
}
